// @ts-nocheck

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
} from "@chakra-ui/react";
import React, { forwardRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import Icon, { XButton } from "../../components/ui-elements/Icon";
import { useMedia } from "../../components/ui-elements/SizeDetector";
// import { RotateLeftIcon, RotateRightIcon, UploadIcon } from "../../icons";

const photoCheckList = [
  "Do you look friendly and professional?",
  "Is your face visible and not cropped?",
  "Will this photo make clients hire you?",
];

type ImageEditorPropsType = {
  image: any;
  cropperOverlayColor: Array<number>;
  width: number;
  height: number;
  isLoading: boolean;
  onSave: () => void;
  onClose: () => void;
  isOpen: boolean;
};

const ImageEditor = forwardRef((props: ImageEditorPropsType, ref) => {
  let [zoomLevel, setZoomLevel] = useState(1.2);
  let [orientation, setOrientation] = useState(0);
  let [photoChecks, setPhotoChecks] = useState([]);
  const isMobile = useMedia("(max-width: 420px)");

  const handleZoom = (value) => {
    setZoomLevel(value);
  };

  const handleRotateLeft = (e) => {
    // e.preventDefault();
    setOrientation(orientation - 90);
  };

  const handleRotateRight = (e) => {
    // e.preventDefault();
    setOrientation(orientation + 90);
  };

  const {
    image,
    cropperOverlayColor = [0, 0, 0, 0.6],
    width = 250,
    height = 250,
    onSave,
    onClose,
    isLoading,
    isOpen,
  } = props;

  React.useEffect(() => {
    setPhotoChecks([]);
  }, [zoomLevel, image, orientation]);

  return isMobile ? (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" isFullHeight>
      <DrawerOverlay />
      <DrawerContent overflow="hidden">
        <DrawerHeader justifyContent="center">
          <Box textAlign="center">
            <Text>Edit photo</Text>
            <Text fontWeight="normal" fontSize="sm">
              Make sure your face is clearly inside the circle
            </Text>
          </Box>
        </DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody px={0}>
          <Editor
            {...{
              orientation,
              image,
              width,
              height,
              ref,
              isLoading,
              cropperOverlayColor,
              zoomLevel,
              handleRotateLeft,
              handleRotateRight,
              handleZoom,
              photoChecks,
              setPhotoChecks,
              onClose,
              onSave,
            }}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  ) : (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent rounded="xl" overflow="hidden" top="-2rem">
        <ModalHeader>
          <Box textAlign="center">
            <Text>Edit photo</Text>
            <Text fontWeight="normal" fontSize="md">
              Make sure your face is clearly inside the circle
            </Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={0}>
          <Editor
            {...{
              orientation,
              image,
              width,
              height,
              ref,
              isLoading,
              cropperOverlayColor,
              zoomLevel,
              handleRotateLeft,
              handleRotateRight,
              handleZoom,
              photoChecks,
              setPhotoChecks,
              onClose,
              onSave,
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

export default ImageEditor;

type EditorPropsType = {
  orientation: any;
  image: any;
  width: number;
  height: number;
  cropperOverlayColor: any;
  zoomLevel: number;
  isLoading: boolean;
  handleRotateLeft: () => void;
  handleRotateRight: () => void;
  handleZoom: () => void;
  photoChecks: any;
  setPhotoChecks: (...params) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
};

const Editor = forwardRef((props: EditorPropsType, ref) => {
  let {
    orientation,
    image,
    width,
    height,
    cropperOverlayColor,
    zoomLevel,
    handleRotateLeft,
    handleRotateRight,
    handleZoom,
    photoChecks,
    isLoading,
    setPhotoChecks,
    onClose,
    onSave,
  } = props;
  return (
    <>
      <Flex
        sx={{
          justifyContent: "center",
          backgroundColor: "#000",
          width: "100%",
          ".uploader__editor__cropper": {
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              cursor: "move",
            },
          },
        }}
      >
        <AvatarEditor
          image={image}
          width={width}
          height={height}
          className="uploader__editor__cropper"
          borderRadius={250}
          ref={ref}
          color={cropperOverlayColor}
          scale={zoomLevel}
          rotate={orientation}
        />
      </Flex>

      <ImageEditorControls
        {...{ handleRotateLeft, handleRotateRight, handleZoom, zoomLevel }}
      />

      <Divider />

      <Stack px={6} spacing={[4, 6]} my={5}>
        <Text fontSize="lg" fontWeight="bold">
          Confirm that this is your best photo:
        </Text>
        <CheckboxGroup
          size="lg"
          colorScheme="blue"
          value={photoChecks}
          onChange={(value) => setPhotoChecks(value)}
        >
          {photoCheckList.map((list, index) => (
            <Checkbox borderColor="neutral.500" key={list} value={list}>
              <Text fontSize="md">{list}</Text>
            </Checkbox>
          ))}
        </CheckboxGroup>

        <Text>If you're unsure, re-upload a better photo.</Text>

        <Stack pt={2} isInline spacing={[3, 5]}>
          <Button isFullWidth variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <XButton
            isDisabled={photoChecks.length < 3}
            isFullWidth
            leftIcon={"upload"}
            isLoading={isLoading}
            loadingText="Uploading"
            colorScheme="blue"
            onClick={onSave}
          >
            Upload
          </XButton>
        </Stack>
      </Stack>
    </>
  );
});

const ImageEditorControls = ({
  handleRotateLeft,
  handleRotateRight,
  handleZoom,
  zoomLevel,
}) => {
  return (
    <Grid
      my={1}
      px={[3, 6]}
      columnGap={3}
      templateColumns="1.5fr 1fr 1fr"
      alignItems="center"
    >
      <Stack align="center" spacing={0} flex={2}>
        <Slider
          max={3}
          min={1}
          label="Zoom"
          step={0.01}
          name="zoom"
          className="slider"
          color="gray2"
          onChange={handleZoom}
          defaultValue={zoomLevel}
        >
          <SliderTrack />
          <SliderFilledTrack />
          <SliderThumb
            height="5"
            width="5"
            borderWidth="xs"
            borderColor="gray.500"
          />
        </Slider>

        <Text fontWeight="medium" fontSize="xs">
          Zoom
        </Text>
      </Stack>

      <Box
        px={3}
        py={2}
        flex={1}
        display="flex"
        flexShrink={0}
        flexDirection="column"
        rounded="md"
        alignItems="center"
        cursor="pointer"
        onClick={handleRotateLeft}
        _hover={{ bg: "gray.100" }}
      >
        <Icon name="rotate-left" size={6} />
        {/* <RotateLeftIcon /> */}
        <Text mt={1} fontWeight="medium" fontSize="xs">
          Left
        </Text>
      </Box>

      <Box
        px={3}
        py={2}
        flex={1}
        display="flex"
        flexShrink={0}
        flexDirection="column"
        rounded="md"
        alignItems="center"
        cursor="pointer"
        onClick={handleRotateRight}
        _hover={{ bg: "gray.100" }}
      >
        <Icon name="rotate-right" size={6} />
        {/* <RotateRightIcon /> */}
        <Text mt={1} fontWeight="medium" fontSize="xs">
          Right
        </Text>
      </Box>
    </Grid>
  );
};
